export type AppRouteManifestEntry = {
  readonly id: string;
  readonly path?: string;
  readonly index?: true;
  readonly availableBeforeAppReady?: true;
  readonly assistant?: {
    readonly label: string;
    readonly description: string;
  };
};

export type AssistantRouteCatalogEntry = {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly pathTemplate: string;
  readonly params: readonly string[];
};

export const BRAND_ROUTE_BASE_TEMPLATE = "/app/brands/:brandId";
export const ACCOUNT_PROFILE_SETTINGS_APP_PATH = "settings/profile";
export const ACCOUNT_PROFILE_SETTINGS_PATH = `/app/${ACCOUNT_PROFILE_SETTINGS_APP_PATH}`;

export const buildAccountProfileSettingsPath = (brandId: string | null | undefined): string =>
  brandId
    ? `${ACCOUNT_PROFILE_SETTINGS_PATH}?brandId=${encodeURIComponent(brandId)}`
    : ACCOUNT_PROFILE_SETTINGS_PATH;

export const extractRouteParams = (pathTemplate: string): string[] =>
  Array.from(pathTemplate.matchAll(/:([A-Za-z0-9_]+)/g), (match) => match[1]!).filter(
    (param, index, params) => params.indexOf(param) === index,
  );

export const buildBrandRoutePathTemplate = (path: string): string =>
  `${BRAND_ROUTE_BASE_TEMPLATE}/${path}`;

export const PUBLIC_APP_ROUTE_MANIFEST = [
  {
    id: "home",
    path: "/",
  },
] as const satisfies readonly AppRouteManifestEntry[];

export const PROTECTED_APP_ROUTE_MANIFEST = [
  {
    id: "app-home",
    index: true,
  },
  {
    id: "account-profile-settings",
    path: ACCOUNT_PROFILE_SETTINGS_APP_PATH,
  },
] as const satisfies readonly AppRouteManifestEntry[];

export const BRAND_APP_ROUTE_MANIFEST = [
  {
    id: "brand-new",
    path: "new",
  },
  {
    id: "brand-onboarding",
    path: "onboarding",
  },
  {
    id: "brand-pulse",
    path: "pulse",
    assistant: {
      label: "Home",
      description: "Brand workspace home and assistant entry point.",
    },
  },
  {
    id: "brand-pulse-chat",
    path: "pulse/:chatId",
  },
  {
    id: "brand-calendar",
    path: "calendar",
    assistant: {
      label: "Calendar",
      description: "Scheduled and generated posts across the brand calendar.",
    },
  },
  {
    id: "brand-review",
    path: "review",
    assistant: {
      label: "Review",
      description: "Campaigns and posts waiting for user review.",
    },
  },
  {
    id: "brand-approvals",
    path: "approvals",
  },
  {
    id: "brand-insights",
    path: "insights",
    assistant: {
      label: "Insights",
      description: "Performance dashboards across brand marketing channels.",
    },
  },
  {
    id: "brand-focus",
    path: "focus",
    assistant: {
      label: "Brand Focus",
      description: "Current brand strategy and strategic focus.",
    },
  },
  {
    id: "brand-campaigns",
    path: "campaigns",
    assistant: {
      label: "Campaigns",
      description: "Campaign list, creation entry point, and campaign status overview.",
    },
  },
  {
    id: "brand-campaign-detail",
    path: "campaigns/:id",
    assistant: {
      label: "Campaign detail",
      description: "Details for a specific campaign when a campaign id is available.",
    },
  },
  {
    id: "brand-campaign-post",
    path: "campaigns/:id/posts/:postId",
    assistant: {
      label: "Campaign post editor",
      description: "Editor for a generated post that belongs to a campaign.",
    },
  },
  {
    id: "brand-standalone-post",
    path: "posts/batches/:batchId/posts/:postId",
    assistant: {
      label: "Standalone post editor",
      description: "Editor for a generated standalone post when batch and post ids are available.",
    },
  },
  {
    id: "brand-assets",
    path: "assets",
    assistant: {
      label: "Media Library",
      description: "Brand media assets and generation reference controls.",
    },
  },
  {
    id: "brand-profile",
    path: "brand-kit/profile",
    availableBeforeAppReady: true,
    assistant: {
      label: "Brand Profile",
      description: "Core identity, positioning, competitors, and audience profile.",
    },
  },
  {
    id: "brand-content-preferences",
    path: "brand-kit/content-preferences",
    availableBeforeAppReady: true,
    assistant: {
      label: "Content Preferences",
      description: "Locale and brand voice preferences for generated content.",
    },
  },
  {
    id: "brand-source-materials",
    path: "brand-kit/source-materials",
    assistant: {
      label: "Source Materials",
      description: "Imported source materials, Instagram media, and reference sources.",
    },
  },
  {
    id: "brand-style",
    path: "brand-kit/style",
    availableBeforeAppReady: true,
    assistant: {
      label: "Brand Style",
      description: "Visual style, colors, typography, imagery, logo, and uploaded fonts.",
    },
  },
  {
    id: "brand-visual-universe",
    path: "brand-kit/visual-universe",
    assistant: {
      label: "Visual Universe",
      description:
        "Photography archetypes, environments, materials, motifs, color roles, and composition guardrails.",
    },
  },
  {
    id: "brand-voice",
    path: "brand-kit/voice",
    availableBeforeAppReady: true,
    assistant: {
      label: "Brand Voice",
      description: "Tone, voice, vocabulary, humor, emoji, and words to avoid or emphasize.",
    },
  },
  {
    id: "brand-settings",
    path: "settings",
    assistant: {
      label: "Autopilot",
      description: "Brand workspace autopilot controls and automation status.",
    },
  },
  {
    id: "brand-settings-billing",
    path: "settings/billing",
    assistant: {
      label: "Billing",
      description: "Brand workspace billing settings and subscription details.",
    },
  },
  {
    id: "brand-integrations",
    path: "integrations",
    assistant: {
      label: "Integrations",
      description: "Connected social accounts and Instagram integration status.",
    },
  },
  {
    id: "brand-integration-instagram",
    path: "integrations/instagram",
    assistant: {
      label: "Instagram",
      description: "Instagram account status, imported posts, and performance metrics.",
    },
  },
] as const satisfies readonly AppRouteManifestEntry[];

export type AppRouteId =
  | (typeof PUBLIC_APP_ROUTE_MANIFEST)[number]["id"]
  | (typeof PROTECTED_APP_ROUTE_MANIFEST)[number]["id"]
  | (typeof BRAND_APP_ROUTE_MANIFEST)[number]["id"];

export const ASSISTANT_ROUTE_CATALOG = BRAND_APP_ROUTE_MANIFEST.flatMap((route) => {
  if (!("assistant" in route) || !route.assistant || !route.path) {
    return [];
  }

  const pathTemplate = buildBrandRoutePathTemplate(route.path);
  return [
    {
      id: route.id,
      label: route.assistant.label,
      description: route.assistant.description,
      pathTemplate,
      params: extractRouteParams(pathTemplate),
    },
  ];
}) satisfies readonly AssistantRouteCatalogEntry[];
